// "What are Treats?" — full-bleed indigo/violet gradient block with a glass jar of bones.
import { CtaPair } from "../components/CtaPair";
import { TreatIcon } from "../components/TreatIcon";

const BONE_POSITIONS: Array<{
  top: number;
  left: number;
  rot: string;
  color: string;
}> = [
  // top layer
  { top: 60, left: 28, rot: "-28deg", color: "#F59E0B" },
  { top: 72, left: 108, rot: "14deg", color: "#FCD34D" },
  { top: 58, left: 150, rot: "-6deg", color: "#FBBF24" },
  // upper-middle
  { top: 96, left: 42, rot: "8deg", color: "#FBBF24" },
  { top: 102, left: 84, rot: "-22deg", color: "#F59E0B" },
  { top: 92, left: 142, rot: "32deg", color: "#FCD34D" },
  // middle
  { top: 130, left: 32, rot: "22deg", color: "#FCD34D" },
  { top: 140, left: 78, rot: "-4deg", color: "#FBBF24" },
  { top: 126, left: 118, rot: "18deg", color: "#F59E0B" },
  { top: 138, left: 160, rot: "-14deg", color: "#FBBF24" },
  // lower-middle
  { top: 168, left: 36, rot: "-18deg", color: "#F59E0B" },
  { top: 174, left: 92, rot: "26deg", color: "#FBBF24" },
  { top: 164, left: 152, rot: "6deg", color: "#FCD34D" },
  // bottom
  { top: 210, left: 30, rot: "10deg", color: "#FBBF24" },
  { top: 216, left: 74, rot: "-32deg", color: "#FCD34D" },
  { top: 208, left: 124, rot: "4deg", color: "#F59E0B" },
  { top: 220, left: 162, rot: "-12deg", color: "#FBBF24" },
];

export function TreatsSection() {
  return (
    <section
      id="treats"
      className="relative overflow-hidden border-t border-b border-indigo-100 bg-[linear-gradient(135deg,#F5F3FF_0%,#EEF2FF_50%,#E0E7FF_100%)] px-[clamp(24px,5vw,80px)] py-[clamp(72px,9vw,128px)]"
    >
      <div className="relative mx-auto max-w-[1120px]">
        <div className="treats-grid relative z-[2] grid grid-cols-[1fr_auto] items-center gap-10">
          <div>
            <div className="mb-5 inline-flex items-center gap-2.5 rounded-full border border-indigo-200 bg-white px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.04em] text-indigo-700">
              <TreatIcon size={14} />
              What are Treats?
            </div>
            <h2 className="font-companion tracking-[-0.01em] m-0 mb-5 text-[clamp(32px,4vw,44px)] font-bold leading-[1.1] tracking-[-0.025em] text-primary">
              Feed your Companion treats.
              <br />
              Watch them work.
            </h2>
            <p className="mt-5 mb-6 max-w-[540px] text-[17px] leading-[1.6] text-grindigo-700 [text-wrap:pretty]">
              Treats fuel your Companions. The harder the task, the more it
              eats.
            </p>
            <div className="flex max-w-[440px] items-center gap-3 rounded-[14px] border border-indigo-200 bg-white px-[18px] py-3.5">
              <TreatIcon size={28} />
              <div className="text-sm leading-[1.4]">
                <b className="text-primary">500 free Treats</b> when you sign
                up.
                <div className="text-[13px] text-secondary">
                  Enough to try everything. On us.
                </div>
              </div>
            </div>

            <CtaPair tone="light" size="md" className="mt-5" />
          </div>

          {/* visual — glass treat jar */}
          <div className="treats-jar relative h-[300px] w-[260px]">
            <div className="absolute inset-0 grid place-items-center">
              <div className="treats-jar-inner relative h-[270px] w-[220px] origin-[50%_100%]">
                <svg
                  viewBox="0 0 220 270"
                  width="220"
                  height="270"
                  className="absolute inset-0"
                >
                  {/* glass body */}
                  <path
                    d="M 28 56 Q 28 46 38 44 L 182 44 Q 192 46 192 56 L 192 240 Q 192 254 178 254 L 42 254 Q 28 254 28 240 Z"
                    fill="#EEF2FF"
                    stroke="#4F46E5"
                    strokeWidth={2.5}
                  />
                  {/* lid */}
                  <rect
                    x={28}
                    y={18}
                    width={164}
                    height={30}
                    rx={6}
                    fill="#4F46E5"
                  />
                  {/* lid cap */}
                  <rect
                    x={24}
                    y={10}
                    width={172}
                    height={14}
                    rx={4}
                    fill="#4338CA"
                  />
                </svg>

                {/* treat bones inside — clipped to the jar interior */}
                <div className="absolute inset-0 [clip-path:inset(56px_30px_18px_30px_round_0_0_16px_16px)] [-webkit-clip-path:inset(56px_30px_18px_30px_round_0_0_16px_16px)]">
                  {BONE_POSITIONS.map((b, i) => (
                    <div
                      key={i}
                      className="treat-bone absolute"
                      style={
                        {
                          top: b.top,
                          left: b.left,
                          "--rot": b.rot,
                          transform: "rotate(var(--rot))",
                        } as React.CSSProperties
                      }
                    >
                      <TreatIcon size={44} color={b.color} />
                    </div>
                  ))}
                </div>

                {/* 500 FREE pill */}
                <div className="absolute -top-1 -right-[22px] z-[2] rotate-[8deg] rounded-full bg-indigo-600 px-3 py-2 text-xs font-bold text-white shadow-[0_8px_20px_-6px_rgba(99,102,241,0.5)]">
                  500 FREE
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
